import { useRef, useState } from 'react';
import { Button, DialogActions, DialogContent, useColorScheme } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import AppDialog from '../../../components/AppDialog';
import InfoButton from '../../../components/InfoButton';
import { useModalHistory } from '../../../utils/useModalHistory';
import { STATUSES } from '../../planning/constants';
import { statusColors, statusColorsDark } from '../../planning/colors';
import type { Machine, RentalExtensionSuggestion } from '../../planning/types';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';
import { formatAddressWithName } from '../../../utils/formatAddress';

function formatAddress(machine: Machine): string | undefined {
  const a = machine.address;
  if (!a) return undefined;
  return formatAddressWithName(a);
}

// `time` comes from a Postgres `time` column, always serialized with seconds ('HH:mm:ss')
// regardless of the 'HH:mm' string the app originally wrote — sliced to the first 5
// characters so both forms combine cleanly with `date` here.
function formatCompactDateTime(date?: string, time?: string): string {
  if (!date) return '';
  const hhmm = (time || '00:00').slice(0, 5);
  return dayjs(`${date}T${hhmm}`).format('D MMM HH:mm');
}

interface ExtensionSuggestionsDialogProps {
  suggestions: RentalExtensionSuggestion[];
  onNavigate: (machineIds: string[]) => void;
  onDismiss: (anchorMachineId: string) => void;
  onClose: () => void;
}

export default function ExtensionSuggestionsDialog({
  suggestions,
  onNavigate,
  onDismiss,
  onClose,
}: ExtensionSuggestionsDialogProps) {
  useModalHistory(onClose);
  const { mode } = useColorScheme();
  const isDark = mode === 'dark';
  const [index, setIndex] = useState(0);
  // "Geen interesse" stays enabled for as long as a suggestion is shown, unlike Vorige/Volgende
  // which can be disabled by the very click that navigates to the first/last suggestion (with
  // only 2 suggestions, that click disables one nav button while the other is *already*
  // disabled) — a safe refocus target so a disabled button never keeps the focus that was on
  // it, which would otherwise make the browser drop focus to <body> and break the dialog's
  // Escape-key/focus-trap handling.
  const stableFocusRef = useRef<HTMLButtonElement>(null);

  const total = suggestions.length;
  // Dismissing the current suggestion shrinks the array from under `index` — clamp instead
  // of tracking it separately, so the counter/buttons never point past the end.
  const currentIndex = Math.min(index, total - 1);
  const suggestion = suggestions[currentIndex];

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={
        <span className="inline-flex items-center gap-1">
          {LABELS.extensionDialogTitle}
          <InfoButton title={LABELS.extensionDialogTitle} description={LABELS.infoExtension} />
        </span>
      }
      testId="extension-suggestions-dialog"
    >
      <DialogContent>
        {!suggestion ? (
          <p className="text-sm text-text-secondary">{LABELS.extensionAllDismissed}</p>
        ) : (
          <>
            <p className="text-xs text-text-secondary mt-0 mb-4">
              {LABELS.extensionSuggestionCounter(currentIndex + 1, total)}
            </p>
            <p className="text-sm font-bold uppercase tracking-wide text-primary mb-2 mt-0">
              {LABELS.extensionCurrentSuggestion}
            </p>
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-border px-4 py-3 flex flex-col gap-0.5">
                <div className="grow grid grid-cols-2 gap-2">
                  <div className="flex flex-col">
                    <div className="flex flex-1 items-center gap-1">
                      <p className="text-base font-semibold mb-0 mt-0 truncate">
                        {suggestion.anchor.name}
                      </p>
                      ·
                      <span className="text-base text-text-secondary truncate">
                        {suggestion.anchor.category}
                      </span>
                    </div>
                    <div className="text-sm text-text-secondary">
                      {formatAddress(suggestion.anchor)}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {suggestion.anchor.supplier?.name}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-end gap-1">
                      <div className="flex items-center gap-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{
                            backgroundColor: (
                              (isDark ? statusColorsDark : statusColors)[
                                suggestion.anchor.status
                              ] ?? (isDark ? statusColorsDark : statusColors).to_order
                            ).dot,
                          }}
                        />
                        <span className="text-sm truncate">
                          {STATUSES[suggestion.anchor.status]?.label ?? suggestion.anchor.status}
                        </span>
                      </div>
                      ·
                      <div className="text-sm text-text-secondary">
                        {LABELS.extensionStarting}{' '}
                        {formatCompactDateTime(
                          suggestion.anchor.start_date,
                          suggestion.anchor.start_time
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-primary mb-2 mt-0">
                  {LABELS.extensionAlternatives}
                </p>
                <div className="flex flex-col gap-2">
                  {suggestion.alternatives.map((alt) => {
                    const isOverlap = alt.gapDays < 0;
                    return (
                      <div
                        key={alt.machine.id}
                        data-testid={`extension-alt-${alt.machine.id}`}
                        className="flex flex-col gap-2 rounded-lg border border-border px-3 py-2"
                      >
                        <div className="grow grid grid-cols-2 gap-2">
                          <div className="flex flex-col">
                            <div className="flex flex-1 items-center gap-1">
                              <p className="text-base font-semibold mb-0 mt-0 truncate">
                                {alt.machine.name}
                              </p>
                              ·
                              <span className="text-base text-text-secondary truncate">
                                {alt.machine.category}
                              </span>
                            </div>
                            <div className="text-sm text-text-secondary">
                              {alt.machine.supplier?.name}
                            </div>
                            {alt.missingOptions.length > 0 && (
                              <div
                                className="flex gap-1 items-center"
                                style={{
                                  color: 'var(--mui-palette-warning-main)',
                                }}
                              >
                                <WarningAmberIcon fontSize="inherit" aria-hidden="true" />
                                <p className="text-sm mb-0 mt-0">
                                  {LABELS.extensionMissingOptions(alt.missingOptions)}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-end gap-1">
                              <div className="flex items-center gap-1">
                                <span
                                  className="w-1.5 h-1.5 rounded-full shrink-0"
                                  style={{
                                    backgroundColor: (
                                      (isDark ? statusColorsDark : statusColors)[
                                        alt.machine.status
                                      ] ?? (isDark ? statusColorsDark : statusColors).to_order
                                    ).dot,
                                  }}
                                />
                                <span className="text-sm truncate">
                                  {STATUSES[alt.machine.status]?.label ?? alt.machine.status}
                                </span>
                              </div>
                              ·
                              <div className="text-sm text-text-secondary">
                                {LABELS.extensionEnding}{' '}
                                {formatCompactDateTime(alt.machine.end_date, alt.machine.end_time)}
                              </div>
                            </div>
                            <div
                              className="flex items-center gap-2 justify-end"
                              style={{
                                color: isOverlap ? 'var(--mui-palette-warning-main)' : undefined,
                              }}
                            >
                              {isOverlap && (
                                <WarningAmberIcon fontSize="inherit" aria-hidden="true" />
                              )}
                              <p className="text-sm mb-0 mt-0">
                                {alt.gapDays === 0
                                  ? LABELS.extensionGapSeamless
                                  : isOverlap
                                    ? LABELS.extensionGapOverlap(Math.abs(alt.gapDays))
                                    : LABELS.extensionGapMargin(alt.gapDays)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      {suggestion && (
        <DialogActions>
          <Button
            onClick={() => {
              if (currentIndex === 1) stableFocusRef.current?.focus();
              setIndex((i) => Math.max(0, i - 1));
            }}
            disabled={currentIndex === 0}
          >
            {LABELS.extensionPrevious}
          </Button>
          <Button
            onClick={() => {
              if (currentIndex === total - 2) stableFocusRef.current?.focus();
              setIndex((i) => Math.min(total - 1, i + 1));
            }}
            disabled={currentIndex === total - 1}
          >
            {LABELS.extensionNext}
          </Button>
          <Button
            ref={stableFocusRef}
            color="inherit"
            onClick={() => onDismiss(suggestion.anchor.id)}
          >
            {LABELS.extensionNotInterested}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              onNavigate([
                suggestion.anchor.id,
                ...suggestion.alternatives.map((a) => a.machine.id),
              ])
            }
          >
            {LABELS.extensionViewInPlanning}
          </Button>
        </DialogActions>
      )}
    </AppDialog>
  );
}
